import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Box,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import RouteTripTime from "./RouteTripTime";

export default function RouteSelection({
  routeType,
  onRouteTypeChange,
  onTripDataChange,
  initialTripData,
  computedTravelMinutes = 0,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [tripData, setTripData] = useState({
    tripDate: null,
    startTime: null,
    endTime: null,
  });
  const [userFeatures, setUserFeatures] = useState([]);

  useEffect(() => {
    if (initialTripData) {
      setTripData(initialTripData);
    }
  }, [initialTripData]);

  useEffect(() => {
    try {
      const localDataString = localStorage.getItem("userState");
      if (localDataString) {
        const localData = JSON.parse(localDataString);
        if (localData?.features) setUserFeatures(localData.features);
      }
    } catch (error) {
      console.error("Error loading features from localStorage:", error);
    }
  }, []);

  const hasPermission = (apiPath, action) => {
    return userFeatures.some(
      (feature) => feature.api_path === apiPath && feature.action === action
    );
  };

  const handleTripDataChange = (newTripData) => {
    setTripData(newTripData);
    onTripDataChange(newTripData);
  };

  useEffect(() => {
    if (!tripData.startTime || !computedTravelMinutes) {
      return;
    }

    const startTime = tripData.startTime;
    if (typeof startTime.add !== "function") {
      return;
    }

    const nextEnd = startTime.add(computedTravelMinutes, "minute");
    if (tripData.endTime && nextEnd.isSame(tripData.endTime, "minute")) {
      return;
    }

    handleTripDataChange({
      ...tripData,
      endTime: nextEnd,
    });
  }, [tripData.startTime, computedTravelMinutes]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2, md: 1 },
          width: "100%",
          minHeight: { xs: "auto", md: "43px" },
          borderRadius: "12px",
          border: "1px solid #C8CBD0",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1.5, md: 0 },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              textAlign: "left",
              color: "#183059",
              fontFamily: "Roboto",
              lineHeight: "100%",
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              width: { xs: "100%", md: "20%" },
              marginBottom: { xs: 0.5, md: 0 },
              flexShrink: 0,
            }}
          >
            {t("Route Type")}:
          </Typography>
          <RadioGroup
            row={!isSmall}
            value={routeType}
            onChange={(e) => onRouteTypeChange(e.target.value)}
            sx={{
              width: { xs: "100%", md: "80%" },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              gap: { xs: 1, sm: 1.5, md: 2 },
              "& .MuiFormControlLabel-root": {
                margin: 0,
              },
            }}
          >
            <FormControlLabel
              value="UNI_DIRCTIONAL"
              control={
                <Radio
                  sx={{
                    color: "#414958",
                    "&.Mui-checked": { color: "#20A8E0" },
                    padding: { xs: "6px", md: "9px" },
                  }}
                />
              }
              label={t("One Way Route")}
              sx={{
                flex: { xs: "0 0 100%", sm: "0 0 auto" },
                justifyContent: "flex-start",
                marginLeft: 0,
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  color: routeType === "UNI_DIRCTIONAL" ? "#20A8E0" : "#414958",
                },
              }}
            />
            <FormControlLabel
              value="BI_DIRECTIONAL"
              control={
                <Radio
                  sx={{
                    color: "#414958",
                    "&.Mui-checked": { color: "#20A8E0" },
                    padding: { xs: "6px", md: "9px" },
                  }}
                />
              }
              label={t("Round Route")}
              sx={{
                flex: { xs: "0 0 100%", sm: "0 0 auto" },
                justifyContent: "flex-start",
                marginLeft: 0,
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  color: routeType === "BI_DIRECTIONAL" ? "#20A8E0" : "#414958",
                },
              }}
            />
            <FormControlLabel
              value="ONE_TIME_DELIVERY"
              control={
                <Radio
                  sx={{
                    color: "#414958",
                    "&.Mui-checked": { color: "#20A8E0" },
                    padding: { xs: "6px", md: "9px" },
                  }}
                />
              }
              label={t("One Time")}
              sx={{
                flex: { xs: "0 0 100%", sm: "0 0 auto" },
                justifyContent: "flex-start",
                marginLeft: 0,
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  color:
                    routeType === "ONE_TIME_DELIVERY" ? "#20A8E0" : "#414958",
                },
              }}
            />
          </RadioGroup>
        </Box>
      </Paper>
      {routeType === "ONE_TIME_DELIVERY" && (
        <RouteTripTime
          tripData={tripData}
          setTripData={handleTripDataChange}
          isUpdateMode={Boolean(initialTripData)}
          endTimeReadOnly={computedTravelMinutes > 0}
        />
      )}
    </Box>
  );
}